import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";
import { Checkbox } from "../../FormElement/Checkbox";

import "./styles.scss";

interface IModalDeleteAccountProps {
  locationData: LocationData;
  onClose: () => void;
  onDeleteComplete: () => void;
}

export const ModalDeleteAccount: React.FC<IModalDeleteAccountProps> = ({
  locationData,
  onClose,
  onDeleteComplete,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string, options?: any) =>
    _t(`facilityManagementPage.modalDeleteAccount.${key}`, options);

  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const accounts = locationData.locations_accounts || [];

  const handleToggleAccount = (accountId: number) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleDeleteClick = () => {
    if (selectedAccountIds.length === 0) {
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedAccountIds.length === 0) {
      return;
    }

    try {
      setIsLoading(true);

      // Delete each selected account
      for (const accountId of selectedAccountIds) {
        await DataSvc.deleteAccountById(accountId);
      }

      onDeleteComplete();
      onClose();
    } catch (error) {
      console.error("Error deleting accounts:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="modal modal-default modal-delete-account">
      <div className="modal-mains">
        <button className="btn-close" onClick={onClose}></button>
        <div className="modal-mains__body">
          <div className="modal-header">
            <h2>{t("title")}</h2>
          </div>

          <div className="warning-message">
            {t("warning", { facility_name: locationData.name })}
          </div>

          <div className="account-selection-section">
            <div className="selection-label">{t("select_accounts")}</div>

            {accounts.length === 0 ? (
              <div className="no-accounts-message">{t("no_accounts")}</div>
            ) : (
              <div className="accounts-list">
                {accounts.map((locationAccount: any) => (
                  <div key={locationAccount.id} className="account-item">
                    <Checkbox
                      id={`account-${locationAccount.account_id}`}
                      checked={selectedAccountIds.includes(
                        locationAccount.account_id
                      )}
                      onChange={() =>
                        handleToggleAccount(locationAccount.account_id)
                      }
                      label={
                        locationAccount.ac_accounts?.email ||
                        `Account ID: ${locationAccount.account_id}`
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              className="btn btn-border"
              onClick={onClose}
              disabled={isLoading}
            >
              {t("cancel")}
            </button>
            <button
              className="btn btn-red"
              onClick={handleDeleteClick}
              disabled={isLoading || selectedAccountIds.length === 0}
            >
              {t("delete")}
            </button>
          </div>
        </div>
      </div>

      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <h3>{t("confirm_title")}</h3>
            <p>{t("confirm_message", { count: selectedAccountIds.length })}</p>
            <div className="confirmation-actions">
              <button
                className="btn btn-border"
                onClick={handleCancelConfirmation}
                disabled={isLoading}
              >
                {t("cancel")}
              </button>
              <button
                className="btn btn-red"
                onClick={handleConfirmDelete}
                disabled={isLoading}
              >
                {isLoading ? t("loading") : t("confirm_delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalDeleteAccount;
